/* eslint-disable @typescript-eslint/no-unused-vars */
import { createTheme } from '@mui/material/styles';

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    primary: true;
    secondary: true;
    altPrimary: true;
    altSecondary: true;
  }
  interface ButtonPropsSizeOverrides {
    extraSmall: true;
    comfy: true;
    puffy: true;
  }
}

const defaultFontFamily = 'Roboto,sans-serif';
const defaultFontSize = 16;
const primaryOutlineBorderColor = '#09b3b0';
const primaryOutlineBorderColorHover = '#09b3b0';
const btnPrimaryColor = '#09b3b0'; /// /Any teal color you see on Keen site (ex. Header background color)
const btnPrimaryColorHover = '#5fcac8';
const btnPrimaryColorActive = '#008584';
const btnPrimaryColorDisabled = '#bdc2cc';
const btnSecondaryColor = '#afb5c2';
const btnSecondaryColorHover = '#989fad';
const btnSecondaryColorActive = '#47686e';
const btnSecondaryColorDisabled = '#f8f9fa';
const btnSecondaryColorDisabledBorder = '#caced6';
const colorWhite = '#ffffff';
const colorBottleGreen = '#09292f';

const theme = createTheme({
  spacing: 4,
  palette: {
    primary: {
      main: btnPrimaryColor, // Any teal color you see on Keen site (ex. Header background color)
      contrastText: colorWhite,
      dark: '#008080FF',
      light: '#f1faf9'
    },
    secondary: {
      main: '#cd4587'
    },
    text: {
      primary: colorBottleGreen, // For any text on Keen site
      disabled: btnPrimaryColorDisabled,
      secondary: '#d1d4db'
    },
    info: {
      main: colorBottleGreen
    }
  },
  typography: {
    fontFamily: defaultFontFamily,
    fontSize: defaultFontSize
  },
  breakpoints: {
    values: {
      xs: 320, // xs <= 320
      sm: 768, // s - mobile <768
      md: 992, // m - tablet 768 - 991
      lg: 1200, // l - small destop 992 - 1199
      xl: 1400 // xl - large desktop >= 1200
      // page length or width <= 1100
    }
  },
  components: {
    MuiContainer: {
      styleOverrides: {
        root: {
          width: '100%',
          textTransform: 'none',
          lineHeight: '1.42857'
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: primaryOutlineBorderColor
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: primaryOutlineBorderColorHover
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: primaryOutlineBorderColorHover
          }
          // color: primaryColor
        }
      }
    },
    MuiButton: {
      variants: [
        {
          props: { variant: 'primary' },
          style: {
            color: colorWhite,
            backgroundColor: btnPrimaryColor,
            borderColor: btnPrimaryColor,
            ':hover': {
              color: colorWhite,
              backgroundColor: btnPrimaryColorHover,
              borderColor: btnPrimaryColorHover
            },
            ':active': {
              color: btnPrimaryColorActive,
              backgroundColor: btnPrimaryColor,
              borderColor: btnPrimaryColorActive,
              shadows: ['none'],
              outline: 0
            },
            ':disabled': {
              backgroundColor: btnPrimaryColorDisabled,
              borderColor: btnPrimaryColorDisabled,
              color: colorWhite
            },
            ':focus': {
              backgroundColor: btnPrimaryColorActive,
              borderColor: btnPrimaryColorActive,
              outline: 0
            }
          }
        },
        {
          props: { variant: 'secondary' },
          style: {
            // background: "rgba(255, 255, 255, .2)",
            color: colorWhite,
            backgroundColor: btnSecondaryColor,
            borderColor: btnSecondaryColor,
            ':hover': {
              color: colorWhite,
              backgroundColor: btnSecondaryColorHover,
              borderColor: btnSecondaryColorHover
            },
            ':active': {
              color: colorWhite,
              backgroundColor: btnSecondaryColorActive,
              borderColor: btnSecondaryColorActive,
              shadows: ['none'],
              outline: 0
            },
            ':disabled': {
              color: colorWhite,
              borderColor: btnSecondaryColorDisabledBorder,
              backgroundColor: btnSecondaryColorDisabledBorder
            },
            ':focus': {
              color: colorWhite,
              backgroundColor: btnSecondaryColorActive,
              borderColor: btnSecondaryColorActive,
              outline: 0
            }
          }
        },
        {
          props: { variant: 'altPrimary' },
          style: {
            color: btnPrimaryColor,
            borderColor: btnPrimaryColor,
            borderRadius: 5,
            ':hover': {
              color: btnPrimaryColorHover,
              borderColor: btnPrimaryColorHover
            },
            ':active': {
              color: btnPrimaryColorActive,
              borderColor: btnPrimaryColorActive,
              shadows: ['none'],
              outline: 0
            },
            ':disabled': {
              color: btnPrimaryColorDisabled,
              borderColor: btnPrimaryColorDisabled
            },
            ':focus': {
              color: btnPrimaryColorActive,
              borderColor: btnPrimaryColorActive,
              outline: 0
            }
          }
        },
        {
          props: { variant: 'altSecondary' },
          style: {
            color: btnSecondaryColor,
            borderColor: btnSecondaryColor,
            backgroundColor: 'transparent',
            ':hover': {
              color: btnSecondaryColorHover,
              borderColor: btnSecondaryColorHover
            },
            ':active': {
              color: btnSecondaryColorActive,
              borderColor: btnSecondaryColorActive,
              shadows: ['none'],
              outline: 0
            },
            ':disabled': {
              color: btnSecondaryColorDisabled,
              borderColor: btnSecondaryColorDisabledBorder
            },
            ':focus': {
              color: btnSecondaryColorActive,
              borderColor: btnSecondaryColorActive,
              outline: 0
            }
          }
        },
        // Custom button size: extraSmall, comfy and puffy matching current Keen theme.
        {
          props: { size: 'extraSmall' },
          style: { padding: '6px 24px', fontSize: '12px', lineHeight: '12px' }
        },
        {
          props: { size: 'comfy' },
          style: { padding: '15px 24px', fontSize: '16px', lineHeight: '16px' }
        },
        {
          props: { size: 'puffy' },
          style: { padding: '22px 32px', fontSize: '20px', lineHeight: '20px' }
        }
      ],
      styleOverrides: {
        root: {
          fontFamily: defaultFontFamily,
          fontSize: defaultFontSize,
          borderRadius: 5,
          textTransform: 'none',
          padding: '6px 12px'
        }
      }
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            background: btnPrimaryColor,
            color: 'white'
          },
          '&.Mui-selected:hover': {
            background: btnPrimaryColor
          }
        }
      }
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          color: '#333',
          '&.Mui-selected': {
            background: btnPrimaryColor,
            color: 'white'
          },
          '&.Mui-selected:hover': {
            background: btnPrimaryColor
          }
        }
      }
    },
    MuiSelect: {
      styleOverrides: {
        outlined: {
          backgroundColor: 'white',
          border: `1px solid ${primaryOutlineBorderColor}`,
          outline: 'none'
        }
      }
    },
    MuiSlider: {
      styleOverrides: {
        markLabel: {
          color: 'black'
        }
      }
    }
  }
});

const modifiedTheme = (aTheme) => {
  const t = aTheme;
  // Custom Headings
  t.typography.h2 = {
    fontSize: '24px',
    [theme.breakpoints.up('sm')]: {
      fontSize: '48px'
    },
    fontFamily: 'Yrsa, serif',
    marginBottom: '.75em',
    fontWeight: 500,
    lineHeight: 1,
    letterSpacing: '-1px',
    textAlign: 'center'
  };
  return {
    ...t
  };
};

export default modifiedTheme(theme);
